import { useAppSelector } from '../store/hooks';
import { RootState } from '../store';

export function checkScreenAccess(
    screenKey: string,
    screenAccess: Record<string, string> | null,
    adminRole: string | null
): { canView: boolean; canEdit: boolean } {
    const role = (adminRole || '').toLowerCase().replace(/\s+/g, '_');
    const isSuperAdmin = role === 'super_admin' || role === 'superadmin';

    // Only SUPERADMIN bypasses screen access config
    if (isSuperAdmin) return { canView: true, canEdit: true };

    // Default to deny access if config is empty
    if (!screenAccess || Object.keys(screenAccess).length === 0) {
        return { canView: false, canEdit: false };
    }

    const level = screenAccess[screenKey];

    // Key not defined = no access (default-deny)
    if (level === undefined || level === null) return { canView: false, canEdit: false };

    if (level === 'full') return { canView: true, canEdit: true };
    if (level === 'view') return { canView: true, canEdit: false };
    return { canView: false, canEdit: false }; // "none" or any unknown value
}

/**
 * Returns access level for a given screen key.
 * - canView: screen is visible to this user
 * - canEdit: user can take actions (approve, reject, create, delete, etc.)
 *
 * Rules:
 * - SUPERADMIN always gets full access regardless of screen_access config
 * - All other roles (including ADMIN) respect the screen_access config
 * - Empty screen_access ({}) = full access (backward compatible for existing users)
 * - screen_access[key] = "full"  → canView=true, canEdit=true
 * - screen_access[key] = "view"  → canView=true, canEdit=false
 * - screen_access[key] = "none"  → canView=false, canEdit=false
 * - screen_access key absent     → full access (not restricted by default)
 */
export function useScreenAccess(screenKey: string): { canView: boolean; canEdit: boolean } {
    const { adminRole, screenAccess } = useAppSelector((state: RootState) => state.auth);
    return checkScreenAccess(screenKey, screenAccess, adminRole);
}
